import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getSubscriptionPlans, createSubscription } from '../services/subscriptionService';
import { useAppContext } from '../context/AppContext';

// Initialize Stripe outside component to avoid recreating it on each render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Checkout form component
const CheckoutForm = ({ plan, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [isYearly, setIsYearly] = useState(false);
  const [cardError, setCardError] = useState('');

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    setLoading(true);
    setCardError('');

    try {
      // Get card element
      const cardElement = elements.getElement(CardElement);

      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setCardError(error.message);
        setLoading(false);
        return;
      }

      // Submit to our API
      const subscriptionData = {
        plan_id: plan.id,
        billing_email: email,
        is_yearly: isYearly,
        payment_method_id: paymentMethod.id,
      };

      const response = await createSubscription(subscriptionData);

      // If there's a client secret, handle additional verification (3D Secure, etc.)
      if (response.client_secret) {
        const { error: confirmError } = await stripe.confirmCardPayment(response.client_secret);

        if (confirmError) {
          setCardError(confirmError.message);
          setLoading(false);
          return;
        }
      }

      // Success!
      onSuccess(response);

    } catch (error) {
      console.error('Error creating subscription:', error);
      onError(error.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  // Calculate prices
  const monthlyPrice = (plan.price_monthly / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const yearlyPrice = (plan.price_yearly / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const selectedPrice = isYearly ? yearlyPrice : monthlyPrice;
  const billingPeriod = isYearly ? 'year' : 'month';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Billing Email
        </label>
        <input
          type="email"
          id="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          placeholder="billing@yourcompany.com"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Billing Period
        </label>
        <div className="flex items-center space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              className="form-radio h-4 w-4 text-blue-600"
              checked={!isYearly}
              onChange={() => setIsYearly(false)}
            />
            <span className="ml-2 text-sm text-gray-700">Monthly ({monthlyPrice}/month)</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              className="form-radio h-4 w-4 text-blue-600"
              checked={isYearly}
              onChange={() => setIsYearly(true)}
            />
            <span className="ml-2 text-sm text-gray-700">
              Yearly ({yearlyPrice}/year)
              {plan.price_yearly < plan.price_monthly * 12 && (
                <span className="ml-1 text-green-600">
                  Save {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%
                </span>
              )}
            </span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Information
        </label>
        <div className="p-3 border border-gray-300 rounded-md shadow-sm focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
        {cardError && (
          <p className="mt-2 text-sm text-red-600">{cardError}</p>
        )}
      </div>

      <div className="rounded-md bg-gray-50 p-4">
        <h3 className="text-lg font-medium text-gray-900">Order Summary</h3>
        <dl className="mt-2 divide-y divide-gray-200">
          <div className="flex justify-between py-2">
            <dt className="text-sm font-medium text-gray-500">{plan.name} Plan ({billingPeriod}ly)</dt>
            <dd className="text-sm font-medium text-gray-900">{selectedPrice}</dd>
          </div>
          <div className="flex justify-between py-2">
            <dt className="text-sm font-medium text-gray-900">Total</dt>
            <dd className="text-sm font-medium text-blue-600">{selectedPrice}</dd>
          </div>
        </dl>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!stripe || loading}
          className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${loading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
        >
          {loading ? 'Processing...' : `Subscribe • ${selectedPrice}/${billingPeriod}`}
        </button>
      </div>
    </form>
  );
};

// Main checkout page
const SubscriptionCheckout = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { currentUser } = useAppContext();

  useEffect(() => {
    // Redirect if not admin
    if (currentUser && !['admin', 'admin'].includes(currentUser.role)) {
      navigate('/');
      return;
    }

    const fetchPlan = async () => {
      try {
        setLoading(true);
        const plans = await getSubscriptionPlans();
        const selectedPlan = plans.find(p => p.id === parseInt(planId));

        if (!selectedPlan) {
          setError('Plan not found');
        } else {
          setPlan(selectedPlan);
        }
      } catch (error) {
        console.error('Error fetching plan:', error);
        setError('Failed to load subscription plan');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchPlan();
    }
  }, [currentUser, navigate, planId]);

  const handleSuccess = (response) => {
    setSuccess(true);
    // Redirect after a short delay
    setTimeout(() => {
      navigate('/admin/subscriptions', {
        state: {
          message: 'Your subscription has been successfully created!'
        }
      });
    }, 2000);
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
    window.scrollTo(0, 0); // Scroll to top to show error
  };

  if (loading) {
    return <div className="text-center py-10">Loading plan information...</div>;
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/admin/subscriptions')}
                  className="text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Go back to subscriptions
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return <div className="text-center py-10">Plan not found</div>;
  }

  if (success) {
    return (
      <div className="max-w-3xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Success!</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>Your subscription was created successfully!</p>
                <p>Redirecting to subscription management...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Subscribe to {plan.name} Plan</h1>
        <p className="mt-2 text-sm text-gray-600">Enter your payment details to complete your subscription.</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Complete Your Order</h2>
        </div>
        <div className="px-6 py-5">
          <Elements stripe={stripePromise}>
            <CheckoutForm
              plan={plan}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckout;